import { useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Pencil } from '@factorialco/f0-react/icons/app'
import {
  AnalyticsDashboardsPageDocument,
  AnalyticsDashboardsPageQuery,
  OneAnalyticsDashboardCategoryEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToRelativeTime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import DeleteDashboardDialog from 'modules/ai_reports/components/DeleteDashboardDialog'
import EditDashboardDialog from 'modules/ai_reports/components/EditDashboardDialog'
import type { DashboardNode } from 'modules/ai_reports/lib/types'
import { useOpenDashboardAction } from 'modules/ai/copilotActions/canvas/dashboards/useOpenDashboardAction'
import { F0Box } from '@factorialco/f0-react'

const DEFAULT_SORTING = { field: 'updatedAt', order: 'desc' } as const

// Only `analytics` is exposed in the listing today; the other categories
// (`communities`, `recruitment`, `trainings`) exist in translations + the
// agent but the corresponding listings haven't shipped yet.
const CATEGORY_VALUES: OneAnalyticsDashboardCategoryEnum[] = ['analytics']

const AUTHOR_SCOPE_ME = 'me'
const AUTHOR_SCOPE_NOT_ME = 'not_me'
type AuthorScope = typeof AUTHOR_SCOPE_ME | typeof AUTHOR_SCOPE_NOT_ME

const DashboardsListSection = () => {
  const { access, company, employee: currentEmployee } = useCurrentSession()
  const currentEmployeeId = currentEmployee?.id
  const currentUserFullName = access?.fullName ?? ''
  const { openDashboardById, openChatForNewDashboard } = useOpenDashboardAction({
    persistedDashboardActions: true,
  })
  const [itemToDelete, setItemToDelete] = useState<DashboardNode | null>(null)
  const [itemToEdit, setItemToEdit] = useState<DashboardNode | null>(null)

  const source = useGraphqlDataCollectionSource({
    document: AnalyticsDashboardsPageDocument,
    search: { enabled: true },
    mapVariables: ({ sortings, filters, search }) => {
      const authorScopeValues = Array.isArray(filters.authorScope) ? filters.authorScope : []
      const rawScope = authorScopeValues[0]
      const scope: AuthorScope | undefined =
        rawScope === AUTHOR_SCOPE_ME || rawScope === AUTHOR_SCOPE_NOT_ME ? rawScope : undefined

      let authorIdFilter: { eq?: string; notEq?: string } | undefined
      if (scope === AUTHOR_SCOPE_ME && currentEmployeeId) {
        authorIdFilter = { eq: currentEmployeeId }
      } else if (scope === AUTHOR_SCOPE_NOT_ME && currentEmployeeId) {
        authorIdFilter = { notEq: currentEmployeeId }
      }

      const trimmedSearch = search?.trim()
      const titleFilter = trimmedSearch ? { contains: trimmedSearch } : undefined

      const filter =
        authorIdFilter || titleFilter
          ? {
              ...(authorIdFilter ? { authorId: authorIdFilter } : {}),
              ...(titleFilter ? { title: titleFilter } : {}),
            }
          : undefined

      const categoryValues = Array.isArray(filters.category) ? filters.category : []
      const categoryRaw = categoryValues[0]
      const category = CATEGORY_VALUES.find((value) => value === categoryRaw)

      return {
        sortOrder: sortings?.[0] ?? DEFAULT_SORTING,
        companyId: company.id,
        category,
        filter,
      }
    },
    mapData: (data: AnalyticsDashboardsPageQuery) => data.oneAnalytics.dashboardsConnection.nodes,
    mapPagination: (data: AnalyticsDashboardsPageQuery) => ({
      total: data.oneAnalytics.dashboardsConnection.totalCount ?? 0,
    }),
    itemOnClick: (item: DashboardNode) => () => {
      if (!openDashboardById) return

      void openDashboardById(item.id)
    },
    defaultSortings: DEFAULT_SORTING,
    sortings: {
      title: { label: i18n.t('ai_reports.dashboards.title_column') },
      authorId: { label: i18n.t('ai_reports.dashboards.created_by') },
      createdAt: { label: i18n.t('ai_reports.dashboards.created_at') },
      updatedAt: { label: i18n.t('ai_reports.dashboards.last_update') },
    },
    filters: {
      category: {
        label: i18n.t('ai_reports.dashboards.category'),
        type: 'in' as const,
        options: {
          options: CATEGORY_VALUES.map((value) => ({
            value,
            label: value,
          })),
        },
      },
      ...(currentEmployeeId
        ? {
            authorScope: {
              label: i18n.t('ai_reports.dashboards.author_scope'),
              type: 'in' as const,
              options: {
                options: [
                  {
                    value: AUTHOR_SCOPE_ME,
                    label: currentUserFullName
                      ? i18n.t('ai_reports.dashboards.author_scope_me', {
                          name: currentUserFullName,
                        })
                      : i18n.t('ai_reports.dashboards.author_scope_me_no_name'),
                  },
                  {
                    value: AUTHOR_SCOPE_NOT_ME,
                    label: i18n.t('ai_reports.dashboards.author_scope_other'),
                  },
                ],
              },
            },
          }
        : {}),
    },
    presets: currentEmployeeId
      ? [
          {
            label: i18n.t('ai_reports.dashboards.author_scope_me_short'),
            filter: { authorScope: [AUTHOR_SCOPE_ME] },
          },
          {
            label: i18n.t('ai_reports.dashboards.author_scope_other_short'),
            filter: { authorScope: [AUTHOR_SCOPE_NOT_ME] },
          },
        ]
      : [],
    paginationType: 'pages',
    perPage: 15,
    itemActions: (item: DashboardNode) => [
      {
        label: i18n.t('ai_reports.dashboards.rename'),
        icon: Pencil,
        onClick: () => setItemToEdit(item),
      },
      {
        label: i18n.t('common.delete'),
        icon: Delete,
        critical: true,
        onClick: () => setItemToDelete(item),
      },
    ],
    primaryActions: () => ({
      label: i18n.t('ai_reports.dashboards.create_report'),
      icon: Add,
      onClick: () => openChatForNewDashboard(),
    }),
  })

  return (
    <F0Box paddingX="md" paddingTop="xl" paddingBottom="md">
      <DeleteDashboardDialog itemToDelete={itemToDelete} setItemToDelete={setItemToDelete} />
      <EditDashboardDialog
        key={itemToEdit?.id}
        itemToEdit={itemToEdit}
        setItemToEdit={setItemToEdit}
      />
      <OneDataCollection
        source={source}
        fullHeight
        emptyStates={{
          'no-data': {
            emoji: '📊',
            title: i18n.t('ai_reports.dashboards.empty_state.no_data.title'),
            description: i18n.t('ai_reports.dashboards.empty_state.no_data.description'),
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('ai_reports.dashboards.title_column'),
                  sorting: 'title' as const,
                  render: (item: DashboardNode) => ({
                    type: 'text' as const,
                    value: item.title,
                  }),
                },
                {
                  label: i18n.t('ai_reports.dashboards.description'),
                  width: 300,
                  render: (item: DashboardNode) => ({
                    type: 'text' as const,
                    value: item.description?.trim() || '-',
                  }),
                },
                {
                  label: i18n.t('ai_reports.dashboards.last_update'),
                  sorting: 'updatedAt' as const,
                  render: (item: DashboardNode) => ({
                    type: 'text' as const,
                    value: dateBackendDateTimeToRelativeTime(item.updatedAt),
                  }),
                },
                {
                  label: i18n.t('ai_reports.dashboards.created_by'),
                  sorting: 'authorId' as const,
                  render: (item: DashboardNode) => {
                    if (!item.author) {
                      return { type: 'text' as const, value: '-' }
                    }
                    return {
                      type: 'person' as const,
                      value: { ...item.author, src: buildUrl(item.author.avatar?.url) },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
    </F0Box>
  )
}

export default DashboardsListSection
